import React, { useState } from 'react';

// Custom SVG icons
const ReceiptIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square">
    <path d="M4 2v20l3-2 2 2 3-2 3 2 2-2 3 2V2l-3 2-2-2-3 2-3-2-2 2-3-2z" />
    <line x1="9" y1="9" x2="15" y2="9" />
    <line x1="9" y1="13" x2="15" y2="13" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClockIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const TrendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const CalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square">
    <rect x="3" y="4" width="18" height="18" rx="0" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="16" y1="2" x2="16" y2="6" />
  </svg>
);

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const BillingHistoryTable = ({ billingHistory, onDownloadInvoice }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice?.id);
    await onDownloadInvoice(invoice?.id);
    setTimeout(() => {
      setDownloadingId(null);
      const content = `FAKTURA\n\nFakturanummer: ${invoice?.invoiceNumber}\nDato: ${invoice?.date}\nBeskrivelse: ${invoice?.description}\nBeløp: ${invoice?.amount}\nStatus: Betalt\n\nNordPass AS\nOrg.nr: 123 456 789\nMVA-nr: NO123456789MVA`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${invoice?.invoiceNumber}.txt`; a?.click();
      URL.revokeObjectURL(url);
    }, 1000);
  };

  const statusConfig = {
    paid:    { label: 'Betalt',    color: '#2EC4A0', bg: 'rgba(46,196,160,0.08)',  Icon: CheckIcon },
    pending: { label: 'Venter',    color: '#F5A623', bg: 'rgba(245,166,35,0.08)',  Icon: ClockIcon },
    failed:  { label: 'Mislyktes',color: '#E85555', bg: 'rgba(232,85,85,0.08)',   Icon: XIcon }
  };

  const totalPaid = billingHistory?.filter(i => i?.status === 'paid')?.length || 0;
  const totalAmount = billingHistory?.filter(i => i?.status === 'paid')?.reduce((acc) => acc + 299, 0) || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Stats strip — color-blocked editorial */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0',
        border: '1px solid rgba(255,255,255,0.06)',
        overflow: 'hidden'
      }}>
        {[
          { Icon: ReceiptIcon, label: 'Betalte fakturaer', value: `${totalPaid} stk`, color: '#2EC4A0', bg: 'rgba(46,196,160,0.06)' },
          { Icon: TrendIcon,   label: 'Totalt betalt',     value: `${totalAmount?.toLocaleString('nb-NO')} NOK`, color: '#4A90E2', bg: 'rgba(74,144,226,0.06)' },
          { Icon: CalIcon,     label: 'Neste faktura',     value: '01/03/2026',  color: '#F5A623', bg: 'rgba(245,166,35,0.06)' },
          { Icon: DocIcon,     label: 'Fakturaformat',     value: 'PDF / A4',    color: '#6B5CE7', bg: 'rgba(107,92,231,0.06)' },
        ]?.map((stat, i) => (
          <div key={i} style={{
            background: stat?.bg,
            borderRight: i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            padding: '20px 20px',
            borderTop: `3px solid ${stat?.color}`
          }}>
            <div style={{ color: stat?.color, marginBottom: '8px' }}>&lt;stat.Icon /&gt;</div>
            <div style={{
              fontFamily: 'Impact, Haettenschweiler, sans-serif',
              fontSize: '1.25rem',
              fontWeight: 900,
              color: stat?.color,
              letterSpacing: '-0.02em',
              lineHeight: 1
            }}>{stat?.value}</div>
            <div style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.6875rem',
              color: '#8A95A3',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginTop: '4px'
            }}>{stat?.label}</div>
          </div>
        ))}
      </div>
      {/* Invoice table */}
      <div style={{
        background: '#111318',
        border: '1px solid rgba(255,255,255,0.06)',
        borderTop: '3px solid #6B5CE7',
        overflow: 'hidden'
      }}>
        {/* Table header */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          background: 'rgba(107,92,231,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '32px', height: '32px',
              background: '#6B5CE7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
              clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 0 100%)'
            }}><ReceiptIcon /></div>
            <div>
              <h3 style={{
                fontFamily: 'Impact, Haettenschweiler, sans-serif',
                fontSize: '1.25rem',
                fontWeight: 900,
                color: '#EEF0F2',
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                margin: 0,
                lineHeight: 1
              }}>Fakturahistorikk</h3>
              <p style={{
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                fontSize: '0.8125rem',
                color: '#8A95A3',
                margin: '2px 0 0'
              }}>Siste 12 måneder</p>
            </div>
          </div>
          <button
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid rgba(107,92,231,0.35)',
              color: '#6B5CE7',
              fontFamily: 'Syne, sans-serif',
              fontSize: '0.6875rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 200ms ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(107,92,231,0.12)'; e.currentTarget.style.borderColor = 'rgba(107,92,231,0.6)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(107,92,231,0.35)'; }}
          >
            <DownloadIcon />
            Last ned alle
          </button>
        </div>

        {/* Empty state */}
        {(!billingHistory || billingHistory?.length === 0) ? (
          <div style={{ padding: '56px 28px', textAlign: 'center' }}>
            <div style={{
              width: '48px', height: '48px',
              background: 'rgba(107,92,231,0.08)',
              border: '2px solid rgba(107,92,231,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
              color: '#6B5CE7'
            }}><ReceiptIcon /></div>
            <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#8A95A3', fontSize: '1rem' }}>Ingen fakturaer ennå</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(15,17,23,0.8)' }}>
                  {['Fakturanummer', 'Dato', 'Beskrivelse', 'Beløp', 'Status', '', '']?.map((col, i) => (
                    <th key={i} style={{
                      padding: '10px 20px',
                      textAlign: i === 3 ? 'right' : 'left',
                      fontFamily: 'Syne, sans-serif',
                      fontSize: '0.5625rem',
                      fontWeight: 800,
                      color: '#8A95A3',
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      borderBottom: '2px solid rgba(107,92,231,0.2)'
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {billingHistory?.map((invoice) => {
                  const status = statusConfig?.[invoice?.status] || statusConfig?.pending;
                  const isHov = hoveredRow === invoice?.id;
                  const isExpanded = expandedInvoice === invoice?.id;
                  return (
                    <React.Fragment key={invoice?.id}>
                      <tr
                        onMouseEnter={() => setHoveredRow(invoice?.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{
                          borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.04)',
                          background: isExpanded
                            ? 'rgba(107,92,231,0.06)'
                            : isHov ? 'rgba(74,144,226,0.04)' : 'transparent',
                          transition: 'background 150ms ease'
                        }}
                      >
                        <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            fontFamily: 'JetBrains Mono, "Courier New", monospace',
                            fontSize: '0.8125rem',
                            color: '#4A90E2',
                            fontWeight: 600
                          }}>{invoice?.invoiceNumber}</span>
                        </td>
                        <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            fontFamily: 'JetBrains Mono, "Courier New", monospace',
                            fontSize: '0.8125rem',
                            color: '#8A95A3'
                          }}>{invoice?.date}</span>
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <span style={{
                            fontFamily: 'Georgia, serif',
                            fontStyle: 'italic',
                            fontSize: '0.875rem',
                            color: '#EEF0F2'
                          }}>{invoice?.description}</span>
                        </td>
                        <td style={{ padding: '16px 20px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <span style={{
                            fontFamily: 'Impact, Haettenschweiler, sans-serif',
                            fontSize: '1.0625rem',
                            fontWeight: 900,
                            color: '#EEF0F2',
                            letterSpacing: '-0.02em'
                          }}>{invoice?.amount}</span>
                        </td>
                        <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: status?.bg,
                            borderLeft: `3px solid ${status?.color}`,
                            padding: '4px 10px',
                            color: status?.color
                          }}>
                            <status.Icon />
                            <span style={{
                              fontFamily: 'Syne, sans-serif',
                              fontSize: '0.6875rem',
                              fontWeight: 800,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase'
                            }}>{status?.label}</span>
                          </div>
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <button
                            onClick={() => setExpandedInvoice(isExpanded ? null : invoice?.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#8A95A3',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.75rem',
                              padding: 0,
                              transition: 'color 150ms ease'
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#4A90E2'}
                            onMouseLeave={e => e.currentTarget.style.color = '#8A95A3'}
                          >
                            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            {isExpanded ? 'Skjul' : 'Vis'}
                          </button>
                        </td>
                        <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleDownload(invoice)}
                            disabled={downloadingId === invoice?.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '7px 14px',
                              background: downloadingId === invoice?.id ? 'rgba(74,144,226,0.15)' : 'transparent',
                              border: '1px solid rgba(74,144,226,0.3)',
                              color: '#4A90E2',
                              fontFamily: 'Syne, sans-serif',
                              fontSize: '0.6875rem',
                              fontWeight: 800,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              cursor: downloadingId === invoice?.id ? 'not-allowed' : 'pointer',
                              transition: 'all 200ms ease',
                              clipPath: 'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 0 100%)'
                            }}
                            onMouseEnter={e => { if (downloadingId !== invoice?.id) e.currentTarget.style.background = 'rgba(74,144,226,0.1)'; }}
                            onMouseLeave={e => { if (downloadingId !== invoice?.id) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <DownloadIcon />
                            {downloadingId === invoice?.id ? 'Laster...' : 'Last ned'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ background: 'rgba(107,92,231,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td colSpan={7} style={{ padding: '0 20px 20px 20px' }}>
                            <div style={{
                              background: 'rgba(15,17,23,0.8)',
                              border: '1px solid rgba(107,92,231,0.15)',
                              borderTop: '2px solid #6B5CE7',
                              padding: '20px 24px',
                              marginTop: '8px'
                            }}>
                              <div style={{
                                fontFamily: 'Impact, Haettenschweiler, sans-serif',
                                fontSize: '0.75rem',
                                fontWeight: 900,
                                color: '#6B5CE7',
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                marginBottom: '16px'
                              }}>Fakturadetail</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                                {[
                                  { label: 'Fakturanummer', value: invoice?.invoiceNumber },
                                  { label: 'Fakturadato', value: invoice?.date },
                                  { label: 'Forfallsdato', value: invoice?.date },
                                  { label: 'Betalingsmetode', value: 'Faktura' },
                                  { label: 'Beskrivelse', value: invoice?.description },
                                  { label: 'Totalt', value: invoice?.amount },
                                ]?.map((item, idx) => (
                                  <div key={idx}>
                                    <div style={{
                                      fontFamily: 'Syne, sans-serif',
                                      fontSize: '0.5625rem',
                                      fontWeight: 800,
                                      color: '#8A95A3',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.15em',
                                      marginBottom: '4px'
                                    }}>{item?.label}</div>
                                    <div style={{
                                      fontFamily: 'JetBrains Mono, "Courier New", monospace',
                                      fontSize: '0.8125rem',
                                      color: '#EEF0F2'
                                    }}>{item?.value}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistoryTable;